// utils/chartColor.util.ts

export interface GenerateChartColorsOptions {
  mode?: 'lighten' | 'darken' | 'alternate'
  variationStep?: number
}

export const generateChartColors = (
  baseColors: string[],
  totalItems: number
): string[] => {

  const sanitizedColors = baseColors.filter(
    (color, index, self) =>
      self.indexOf(color) === index
  )

  const colors: string[] = []

  for (let i = 0; i < totalItems; i++) {

    const baseColor =
      sanitizedColors[
        i % sanitizedColors.length
      ]

    if (i < sanitizedColors.length) {
      colors.push(baseColor)
      continue
    }

    const cycle =
      Math.floor(i / sanitizedColors.length)

    colors.push(
      cycle % 2 === 0
        ? lightenColor(baseColor, cycle * 22)
        : darkenColor(baseColor, cycle * 22)
    )
  }

  return colors
}

export const generateColorVariant = (
  hex: string,
  cycle: number,
  mode: 'lighten' | 'darken' | 'alternate',
  variationStep: number
): string => {

  const amount = cycle * variationStep

  switch (mode) {

    case 'lighten':
      return lightenColor(hex, amount)

    case 'darken':
      return darkenColor(hex, amount)

    case 'alternate':
    default:
      return cycle % 2 === 0
        ? lightenColor(hex, amount)
        : darkenColor(hex, amount)
  }
}

const clamp = (value: number): number =>
  Math.max(0, Math.min(255, value))

const hexToRgb = (
  hex: string
): { r: number, g: number, b: number } => {

  const sanitized =
    hex.replace('#', '')

  return {
    r: parseInt(sanitized.substring(0, 2), 16),
    g: parseInt(sanitized.substring(2, 4), 16),
    b: parseInt(sanitized.substring(4, 6), 16),
  }
}

const rgbToHex = (
  r: number,
  g: number,
  b: number
): string => {

  return (
    '#' +
    [r, g, b]
      .map(v =>
        clamp(v)
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  )
}

export const lightenColor = (
  hex: string,
  amount: number
): string => {

  const { r, g, b } = hexToRgb(hex)

  return rgbToHex(
    r + amount,
    g + amount,
    b + amount
  )
}

export const darkenColor = (
  hex: string,
  amount: number
): string => {

  const { r, g, b } = hexToRgb(hex)

  return rgbToHex(
    r - amount,
    g - amount,
    b - amount
  )
}